export const ALERT_RULE_TITLE_MAPPER = {
  hvoFuel: 'Fuel Detection',
  logisticsWindow: 'Logistics Window',
  materialCertifications: 'Material Certifications',
  materialThresholds: 'Material Thresholds',
  materialWatchlist: 'Material Watchlist',
  supplierWatchlist: 'Supplier Watchlist',
  wasteCompliance: 'Waste Compliance'
}

export const ALERT_RULE_GROUPING = {
  hvoFuel: 'Quality Assurance',
  logisticsWindow: 'Quality Assurance',
  materialCertifications: 'Material Certifications',
  materialThresholds: 'Quality Assurance',
  materialWatchlist: 'Quality Assurance',
  supplierWatchlist: 'Quality Assurance',
  wasteCompliance: 'Waste Compliance'
}

export const ALERT_RULE_GROUPS = [
  'Quality Assurance',
  'Material Certifications',
  'Waste Compliance'
] as const
export type AlertRuleGroup = (typeof ALERT_RULE_GROUPS)[number]

export const DEFAULT_TIME_RANGE = {
  start: '08:00',
  end: '18:00'
}

const COMMON_DEFAULT_ALERT_PROPS = {
  isEnabled: true,
  recipients: []
}

export const DEFAULT_ALERT_PROPS = {
  hvoFuel: {
    ...COMMON_DEFAULT_ALERT_PROPS
  },
  logisticsWindow: {
    ...COMMON_DEFAULT_ALERT_PROPS,
    schedule: {
      monday: {
        isAllowed: true,
        start: '08:00',
        end: '18:00'
      },
      tuesday: {
        isAllowed: true,
        start: '08:00',
        end: '18:00'
      },
      wednesday: {
        isAllowed: true,
        start: '08:00',
        end: '18:00'
      },
      thursday: {
        isAllowed: true,
        start: '08:00',
        end: '18:00'
      },
      friday: {
        isAllowed: true,
        start: '08:00',
        end: '18:00'
      },
      saturday: {
        isAllowed: false,
        start: null,
        end: null
      },
      sunday: {
        isAllowed: false,
        start: null,
        end: null
      }
    }
  },
  materialThresholds: {
    ...COMMON_DEFAULT_ALERT_PROPS,
    thresholds: []
  },
  materialWatchlist: {
    ...COMMON_DEFAULT_ALERT_PROPS,
    watchedMaterials: []
  },
  supplierWatchlist: {
    ...COMMON_DEFAULT_ALERT_PROPS,
    watchedSuppliers: []
  },
  wasteCompliance: {
    ...COMMON_DEFAULT_ALERT_PROPS
  },
  materialCertifications: {
    ...COMMON_DEFAULT_ALERT_PROPS,
    timberRequiredCertificate: 'FSC'
  }
}

// These types aren't used everywhere but we can gradually add typings to places currently using any
export type WasteComplianceAlertRule = {
  isEnabled: boolean
  recipients: string[]
  isCarrierLicenceEnabled: boolean
  isCarrierNameEnabled: boolean
  isContainerTypeEnabled: boolean
  isEWCCodeEnabled: boolean
  isFacilityAddressEnabled: boolean
  isFacilityNameEnabled: boolean
  isFacilityPermitEnabled: boolean
  isSICCodeEnabled: boolean
}

export type MaterialCertificationsAlertRule = {
  isEnabled: boolean
  recipients: string[]
  isBricksAndBlocksBESOrISOEnabled: boolean
  isConcreteBESOrISOEnabled: boolean
  isInsulationISOEnabled: boolean
  isPlasterboardBESOrISOEnabled: boolean
  isRebarCARESEnabled: boolean
  isStructuralSteelBESOrISOEnabled: boolean
  isTimberEnabled: boolean
  timberRequiredCertificate: MaterialCertificationsAlertRuleTimberRequiredCertType
  timberClaimEvidencedOnProduct: boolean
}

export const MATERIAL_CERTIFICATION_ALERT_RULE_TIMBER_CERT_TYPES = [
  'FSC',
  'PEFC',
  'FSCOrPEFC'
] as const
export type MaterialCertificationsAlertRuleTimberRequiredCertType =
  (typeof MATERIAL_CERTIFICATION_ALERT_RULE_TIMBER_CERT_TYPES)[number]

export function determineAlertRuleCount(
  alertRuleKey: string,
  alertRule: any
): number {
  if (!alertRule) return 0

  if (alertRuleKey === 'wasteCompliance') {
    return determineEnabledWasteComplianceAlertCount(
      alertRule as WasteComplianceAlertRule
    )
  }
  if (alertRuleKey === 'materialCertifications') {
    return determineEnabledMaterialCertificationsAlertCount(
      alertRule as MaterialCertificationsAlertRule
    )
  }

  return (alertRule as { isEnabled?: boolean }).isEnabled === true ? 1 : 0
}

export function determineEnabledWasteComplianceAlertCount(
  wasteComplianceAlertRule: WasteComplianceAlertRule
): number {
  return (
    (wasteComplianceAlertRule.isCarrierLicenceEnabled === true ? 1 : 0) +
    (wasteComplianceAlertRule.isCarrierNameEnabled === true ? 1 : 0) +
    (wasteComplianceAlertRule.isContainerTypeEnabled === true ? 1 : 0) +
    (wasteComplianceAlertRule.isEWCCodeEnabled === true ? 1 : 0) +
    (wasteComplianceAlertRule.isFacilityAddressEnabled === true ? 1 : 0) +
    (wasteComplianceAlertRule.isFacilityNameEnabled === true ? 1 : 0) +
    (wasteComplianceAlertRule.isFacilityPermitEnabled === true ? 1 : 0) +
    (wasteComplianceAlertRule.isSICCodeEnabled === true ? 1 : 0)
  )
}

export function determineEnabledMaterialCertificationsAlertCount(
  materialCertificationsAlertRule: MaterialCertificationsAlertRule
): number {
  return (
    (materialCertificationsAlertRule.isBricksAndBlocksBESOrISOEnabled === true
      ? 1
      : 0) +
    (materialCertificationsAlertRule.isConcreteBESOrISOEnabled === true
      ? 1
      : 0) +
    (materialCertificationsAlertRule.isInsulationISOEnabled === true ? 1 : 0) +
    (materialCertificationsAlertRule.isPlasterboardBESOrISOEnabled === true
      ? 1
      : 0) +
    (materialCertificationsAlertRule.isRebarCARESEnabled === true ? 1 : 0) +
    (materialCertificationsAlertRule.isStructuralSteelBESOrISOEnabled === true
      ? 1
      : 0) +
    (materialCertificationsAlertRule.isTimberEnabled === true ? 1 : 0)
  )
}
